/**
 * useBatchAddInvestments — POSTs a list of investments to /api/investments/batch.
 * Backend validates all rows first; if any fails, NOTHING is inserted and the
 * per-row errors are surfaced so the user can fix them.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { fetchWithAuth } from '../../utils/api';
import { buildInvestmentSubmitData } from '../investmentFormUtils';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useBatchAddInvestments = (onSuccess) => {
  const [isLoading, setIsLoading] = useState(false);
  const [rowErrors, setRowErrors] = useState({});

  const submitBatch = async (rows) => {
    setIsLoading(true);
    setRowErrors({});
    try {
      const investments = rows.map((r) => buildInvestmentSubmitData(r, false));
      const r = await fetchWithAuth(`${BACKEND_URL}/api/investments/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ investments }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const errs = data?.detail?.row_errors;
        if (Array.isArray(errs)) {
          const mapped = {};
          errs.forEach((e) => { mapped[e.row - 1] = e.errors; });
          setRowErrors(mapped);
          toast.error('Some rows have errors. Please fix the highlighted entries.');
        } else {
          toast.error(typeof data?.detail === 'string' ? data.detail : 'Batch save failed.');
        }
        return false;
      }
      toast.success(`${data.created_count} investment${data.created_count === 1 ? '' : 's'} added successfully.`);
      onSuccess?.();
      return true;
    } catch (e) {
      logger.error('Batch add error:', e);
      toast.error(e.message || 'Batch save failed.');
      return false;
    } finally { setIsLoading(false); }
  };

  return { isLoading, rowErrors, submitBatch };
};
